import React, { useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth } from '@/integrations/firebase/auth';
import { db } from '@/integrations/firebase/firestore';
import { isAdmin as isAllowlistedAdmin } from '@/lib/admin';
import {
  identifyUser,
  resetUser,
  setAnalyticsConsent,
} from '@/lib/analytics';
import { profileNeedsOnboarding } from '@/lib/onboarding-gate';
import {
  AuthContext,
  type AuthContextProps,
  type UserProfile,
} from '@/context/auth-context';

interface Props {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Start true so the first authenticated render never treats a missing
  // profile as "needs onboarding" before the Firestore listener attaches.
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Mark profile as loading in the *same* state update batch as the user
      // becoming available. Otherwise one render runs with user set, profile
      // still null, and profileLoading=false — which used to redirect
      // /:drug → /onboarding → / (losing the deep link on every refresh).
      if (currentUser) {
        setProfile(null);
        setProfileLoading(true);
      } else {
        setProfile(null);
        setProfileLoading(false);
        setAnalyticsConsent(false);
        resetUser();
      }
      setUser(currentUser);
      let isAdmin = isAllowlistedAdmin(currentUser);
      if (currentUser && !isAdmin) {
        try {
          // Reads the cached ID token (no network unless expired), so this
          // doesn't meaningfully delay app load.
          const result = await currentUser.getIdTokenResult();
          isAdmin = result.claims.admin === true;
        } catch (error) {
          console.error('Failed to read ID token claims:', error);
        }
      }
      setAdmin(isAdmin);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    const ref = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Omit<UserProfile, 'uid'>;
          setProfile({ uid: user.uid, ...data });
          setAnalyticsConsent(data.analytics_opt_in === true);
          identifyUser(user.uid, data.specialty ?? undefined);
        } else {
          setProfile({ uid: user.uid });
          setAnalyticsConsent(false);
          identifyUser(user.uid);
        }
        setProfileLoading(false);
      },
      (error) => {
        console.error('Failed to load user profile:', error);
        setProfile({ uid: user.uid });
        setProfileLoading(false);
      },
    );

    return unsubscribe;
  }, [user]);

  const value: AuthContextProps = {
    user,
    loading,
    profile,
    profileLoading,
    // Only gate onboarding after the profile snapshot has arrived. A null
    // profile while loading must not look like "missing PWZ".
    needsOnboarding:
      Boolean(user) &&
      !profileLoading &&
      profileNeedsOnboarding(profile, { admin }),
    admin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
